import React, { useContext, useEffect, useMemo } from "react";
import cx from "classnames";
import { AppContext } from "../../context/EditSessionsContext";
import { Section, Session } from "@models/ISchema";
import { Types } from "../../context/result.reducer";
import { EditedSession } from "../../context/formData.reducer";
import { Week } from "../../context/linkData.reducer";
import { Accordion as SuiteAccordion } from "@foris/avocado-suite";
import SessionCard from "../session-card";
import ScheduledBlocksBadge from "../scheduled-blocks-badge";
import { getSectionAssignedBlocks, hasAnyScheduledBlocks } from "@utils/scheduledBlocks";
import { useProgrammingEditStore } from "@context/useProgrammingEditStore";
import styles from "./savedSectionsList.module.scss";

type SessionsBySectionIdType = {
  [key: string]: {
    edited: (Session | EditedSession)[];
    created: (Session | EditedSession)[];
    deleted: (Session | EditedSession)[];
  };
};

const SavedSectionsList = () => {
  const { state, dispatch } = useContext(AppContext);
  const selectSession = state?.result?.selectSession;
  const selectNewSession = state?.result?.selectCreateSession;
  const sessionToDeleteSelected = state?.result?.sessionToDeleteSelected;

  // Programming mode
  const isProgrammingMode = useProgrammingEditStore(s => s.isProgrammingResultsActive);
  const previewSections = useProgrammingEditStore(s => s.previewSections);
  const programmingChangeset = useProgrammingEditStore(s => s.programmingChangeset);

  const linkWeeksById = useMemo(() => {
    return (state?.link?.weeks ?? []).reduce((acc: Record<string, Week>, week: Week) => {
      acc[week.id] = week;
      return acc;
    }, {});
  }, [state?.link?.weeks]);

  // --- Programming mode: build sections from preview ---
  const programmingSectionsBySectionId = useMemo(() => {
    if (!isProgrammingMode || !previewSections?.length) return null;

    const deletedSectionIds = new Set<string>(
      programmingChangeset?.changesets?.deletes?.sections ?? [],
    );

    const result = previewSections.reduce((acc, section) => {
      acc[section.id] = {
        edited: [],
        created: section.sessions.map(session => ({
          id: session.id,
          isNew: true,
          section: {
            id: section.id,
            code: section.code,
            component: section.component,
            courseComponent: { component: section.component },
            origin: null,
            buildings: [],
            sessions: [],
          },
          blocks: session.assignment?.blockRange
            ? {
                selected: "hours" as const,
                day: session.assignment.day,
                startTime: session.assignment.blockRange.start?.startingTime,
                endTime: session.assignment.blockRange.end?.endingTime,
              }
            : null,
          classrooms: session.classrooms ?? [],
          instructors: session.instructors ?? [],
          intervals: (session.assignment?.intervals ?? []).map(i => {
            const linkWeek = linkWeeksById[i.id];
            return { ...i, ...linkWeek, checked: true };
          }),
        })) as any[],
        deleted: [],
      };
      return acc;
    }, {} as SessionsBySectionIdType);

    (state?.link?.sections ?? []).forEach((section: Section) => {
      if (!deletedSectionIds.has(section.id)) return;
      result[section.id] = {
        edited: [],
        created: [],
        deleted: section.sessions as any[],
      };
    });

    return result;
  }, [
    isProgrammingMode,
    previewSections,
    programmingChangeset,
    state?.link?.sections,
    linkWeeksById,
  ]);

  const programmingValidationErrors = useMemo(() => {
    if (!isProgrammingMode) return {};
    const sessionsPayload = state?.result?.resultValidation?.sessionsPayload ?? [];
    return sessionsPayload.reduce(
      (acc, payload) => {
        const hasErrors =
          Boolean(payload?.intervalValidationErrors?.length) ||
          Boolean(payload?.validationErrors?.length) ||
          Boolean(payload?.customValidations?.length);
        if (hasErrors) {
          if (payload?.sessionId) acc[payload.sessionId] = true;
          if (payload?.sessionUuid) acc[payload.sessionUuid] = true;
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [isProgrammingMode, state?.result?.resultValidation]);

  // --- Assignment mode ---
  const savedSessionsToCreateIds = useMemo(() => {
    const ids = {};
    if (state?.form?.sessionsToCreate) {
      for (const session of state.form.sessionsToCreate) {
        if (session?.id) ids[session.id] = true;
      }
    }
    return ids;
  }, [state?.form?.sessionsToCreate]);

  const sessionsWithErrorsById = useMemo(() => {
    if (isProgrammingMode) return programmingValidationErrors;

    const sessionsPayload = [
      ...(state?.result?.createValidation?.sessionsPayload ?? []),
      ...(state?.result?.resultValidation?.sessionsPayload ?? []),
    ];

    return sessionsPayload.reduce((acc, payload) => {
      const hasErrors =
        Boolean(payload?.intervalValidationErrors?.length) ||
        Boolean(payload?.validationErrors?.length) ||
        Boolean(payload?.customValidations?.length);

      if (hasErrors) {
        const id =
          payload?.sessionUuid in savedSessionsToCreateIds
            ? payload?.sessionUuid
            : payload?.sessionId;
        acc[id] = true;
      }
      return acc;
    }, {});
  }, [
    isProgrammingMode,
    programmingValidationErrors,
    state?.result?.createValidation?.sessionsPayload,
    state?.result?.resultValidation?.sessionsPayload,
    savedSessionsToCreateIds,
  ]);

  const sectionsById = useMemo(() => {
    if (isProgrammingMode) {
      const deletedSectionIds = new Set<string>(
        programmingChangeset?.changesets?.deletes?.sections ?? [],
      );

      const result = (previewSections ?? []).reduce((acc, section) => {
        acc[section.id] = {
          id: section.id,
          code: section.code,
          component: section.component,
          courseComponent: { component: section.component },
        };
        return acc;
      }, {});

      (state?.link?.sections ?? []).forEach((section: Section) => {
        if (deletedSectionIds.has(section.id)) result[section.id] = section;
      });

      return result;
    }

    const sessions = [
      ...(state?.form?.savedSessions ?? []),
      ...(Object.values(state?.form?.sessionsToDelete) ?? []),
      ...(state?.form?.sessionsToCreate ?? []),
    ];

    return sessions.reduce((acc, session: EditedSession) => {
      const sectionId = session?.section?.id || session?.session?.section?.id;
      const section = session?.section || session?.session?.section;
      if (sectionId && section) acc[sectionId] = section;
      return acc;
    }, {});
  }, [
    isProgrammingMode,
    previewSections,
    programmingChangeset,
    state?.link?.sections,
    state?.form?.savedSessions,
    state?.form?.sessionsToDelete,
    state?.form?.sessionsToCreate,
  ]);

  const sessionsBySectionId = useMemo(() => {
    if (isProgrammingMode) return programmingSectionsBySectionId ?? {};

    const sessions = [
      ...(state?.form?.savedSessions ?? []),
      ...(Object.values(state?.form?.sessionsToDelete) ?? []),
      ...(state?.form?.sessionsToCreate ?? []),
    ];

    return sessions.reduce((acc, session: EditedSession) => {
      const sectionId = session?.section?.id || session?.session?.section?.id;
      if (!acc[sectionId]) acc[sectionId] = { edited: [], created: [], deleted: [] };
      if (state?.form?.sessionsToDelete?.[session.id]) {
        acc[sectionId].deleted.push(session);
      } else if (session.id.includes("-")) {
        acc[sectionId].created.push(session);
      } else {
        acc[sectionId].edited.push(session.session);
      }
      return acc;
    }, {} as SessionsBySectionIdType);
  }, [
    isProgrammingMode,
    programmingSectionsBySectionId,
    state?.form?.savedSessions,
    state?.form?.sessionsToDelete,
    state?.form?.sessionsToCreate,
  ]);

  // CHS-2785: full (adapted) sections carry courseComponent.scheduledBlocksCount and all
  // their sessions — needed to compute the section total in the result sidebar, which
  // otherwise only holds the changed sessions.
  const fullSectionById = useMemo(
    () =>
      (state?.link?.sections ?? []).reduce((acc: Record<string, Section>, section: Section) => {
        acc[section.id] = section;
        return acc;
      }, {}),
    [state?.link?.sections],
  );

  const showScheduledBlocks = useMemo(
    () => !isProgrammingMode && hasAnyScheduledBlocks(state?.link?.sections ?? []),
    [isProgrammingMode, state?.link?.sections],
  );

  useEffect(() => {
    if (selectSession) dispatch({ type: Types.SelectSession, payload: selectSession });
    if (selectNewSession) dispatch({ type: Types.SelectCreateSession, payload: selectNewSession });
  }, [selectSession, selectNewSession]);

  // In assignment mode, skip render if nothing to show
  if (
    !isProgrammingMode &&
    !state?.form?.savedSessions?.length &&
    !Object.keys(state?.form?.sessionsToDelete)?.length &&
    !state?.form?.sessionsToCreate?.length
  ) {
    return null;
  }

  // In programming mode, skip render if nothing to show
  if (isProgrammingMode && !Object.keys(sessionsBySectionId).length) return null;

  const getSectionTitle = (sectionId: string) => {
    const section = sectionsById?.[sectionId];
    const component = section?.component ?? section?.courseComponent?.component;
    return `${component?.code ? `${component?.code} ` : ""}${section?.code ?? ""}`;
  };

  const hasSectionBlocksCount = (sectionId: string) =>
    showScheduledBlocks && !!fullSectionById[sectionId]?.courseComponent?.scheduledBlocksCount;

  const renderScheduledBlocksBadge = (sectionId: string) => {
    if (!showScheduledBlocks) return undefined;

    const section = fullSectionById[sectionId];
    if (!section) return undefined;

    const createdSessions = (state?.form?.sessionsToCreate ?? []).filter(
      (session: EditedSession) =>
        (session?.section?.id ?? session?.session?.section?.id) === sectionId,
    );

    const assigned = getSectionAssignedBlocks(
      section,
      state?.form?.savedSessions ?? [],
      createdSessions,
      state?.form?.sessionsToDelete ?? {},
      state?.link?.blockRanges?.end,
    );

    return (
      <ScheduledBlocksBadge
        className={styles.scheduledBlocksBadge}
        assigned={assigned}
        planned={section?.courseComponent?.scheduledBlocksCount ?? null}
      />
    );
  };

  return (
    <>
      {Object.entries(sessionsBySectionId).map(([sectionId, sessions], index) => (
        <SuiteAccordion
          key={sectionId}
          title={getSectionTitle(sectionId)}
          opened={index === 0}
          className={cx(styles.savedSectionsList, {
            [styles.savedSectionsList__hasBlocksCount]: hasSectionBlocksCount(sectionId),
          })}
          customContent={renderScheduledBlocksBadge(sectionId)}
        >
          <div className={styles.savedSectionsList_content}>
            {sessions.edited.map(session => (
              <SessionCard
                key={session.id}
                colorIndex={index + 1}
                session={session}
                linkWeeks={state.link.weeks}
                isSelected={session?.id === selectSession?.id}
                status={sessionsWithErrorsById?.[session?.id] ? "error" : "success"}
                onClick={session => {
                  if (session?.id !== selectSession?.id) {
                    dispatch({ type: Types.SelectSession, payload: session as Session });
                  }
                }}
              />
            ))}

            {sessions.created.map(session => (
              <SessionCard
                key={session.id}
                colorIndex={index + 1}
                session={session}
                linkWeeks={state.link.weeks}
                isSelected={session?.id === selectNewSession?.id}
                status={sessionsWithErrorsById?.[session?.id] ? "error" : "success"}
                onClick={session => {
                  if (session?.id !== selectNewSession?.id) {
                    dispatch({ type: Types.SelectCreateSession, payload: session as Session });
                  }
                }}
              />
            ))}

            {sessions.deleted.map(session => (
              <SessionCard
                key={session.id}
                colorIndex={index + 1}
                session={session}
                linkWeeks={state.link.weeks}
                isSelected={session?.id === sessionToDeleteSelected?.id}
                isDeleted
                status={sessionsWithErrorsById?.[session?.id] ? "error" : undefined}
                onClick={session => {
                  if (session?.id !== sessionToDeleteSelected?.id) {
                    dispatch({ type: Types.SelectDeletedSession, payload: session });
                  }
                }}
              />
            ))}
          </div>
        </SuiteAccordion>
      ))}
    </>
  );
};

export default SavedSectionsList;
